import {
  Bookmark, Headset, Lock, LogOut, StickyNote, Trash2,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useDeleteEntryTemplateMutation, useEntryTemplatesQuery } from "@/features/accounting/hooks/useAccountingQueries";
import { logout } from "@/features/auth/store/sessionSlice";
import { ROUTE_REGISTRY, checkFeatureRequirement, checkRouteReadPermission } from "@/shared/configs/routeRegistry.config";
import { ACCOUNTING_MOVEMENTS_PAGE_PATH } from "@/shared/configs/routes.config";
import { useHelpEntryPath } from "@/shared/hooks/useHelpCenterEnabled";
import { useOrganizationFeatures } from "@/shared/hooks/useOrganizationFeatures";
import { useRepresentativePermissions } from "@/shared/hooks/useRepresentativePermissions";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/shared/ui/Command";
import { getContentFeatureAvailability, isContentFeatureAvailable } from "@/shared/utils/contentFeatureAvailability.utils";

type Props = {
  openSearch: boolean
  setOpenSearch: (openSearch: boolean) => void
  placeholderInput?: string
};

type ActionItem = {
  value: string
  label: string
  keywords: string[]
};

type SearchablePage = {
  value: string
  label: string
  path: string
  category: string
  keywords: string[]
  locked: boolean
};

const actionItems: ActionItem[] = [
  {
    value: "contact",
    label: "Centre d'aide",
    keywords: ["contact", "yeati", "aide", "support", "helpdesk", "centre"],
  },
  {
    value: "logout",
    label: "Déconnexion",
    keywords: ["logout", "deconnexion", "sortir", "quitter"],
  },
];

const Search = ({ openSearch, setOpenSearch, placeholderInput = "Chercher une page" }: Props) => {
  const navigate = useNavigate();
  const helpEntryPath = useHelpEntryPath();
  const dispatch = useDispatch();
  const features = useOrganizationFeatures();
  const { permissions } = useRepresentativePermissions();
  const { data: entryTemplatesData } = useEntryTemplatesQuery();
  const entryTemplates = entryTemplatesData?.accounting_entry_templates ?? [];
  const deleteTemplateMutation = useDeleteEntryTemplateMutation();

  const handleDeleteTemplate = (e: React.MouseEvent, templateId: string, templateName: string) => {
    e.stopPropagation();
    deleteTemplateMutation.mutate(templateId, {
      onSuccess: () => toast.success(`"${templateName}" supprimé`),
      onError: () => toast.error("Erreur lors de la suppression"),
    });
  };

  const searchablePages = useMemo(() => {
    const planFeatures = features.plan?.features;
    if (!planFeatures) return [];

    const isUpsellEnabled = !!planFeatures.upsell_promotion;

    return Object.values(ROUTE_REGISTRY)
      .reduce<SearchablePage[]>((acc, route) => {
        if (!route.search) return acc;
        if (typeof route.feature === "string" && !isContentFeatureAvailable(route.feature, getContentFeatureAvailability(planFeatures))) return acc;
        const hasAccess = checkFeatureRequirement(route.feature, planFeatures);
        if (!hasAccess && !isUpsellEnabled) return acc;

        const hasReadPermission = checkRouteReadPermission(route, permissions ?? null);
        if (hasAccess && !hasReadPermission) return acc;

        acc.push({
          value: route.search.value,
          label: route.search.label,
          path: route.path,
          category: route.search.category,
          keywords: route.search.keywords,
          locked: !hasAccess,
        });
        return acc;
      }, []);
  }, [features.plan?.features, permissions]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpenSearch(!openSearch);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [openSearch, setOpenSearch]);

  const handleSelect = (value: string) => {
    const page = searchablePages.find((p) => p.value === value);
    if (page) {
      navigate(page.path);
    }
    setOpenSearch(false);
  };

  const handleTemplateSelect = (templateId: string) => {
    navigate(`${ACCOUNTING_MOVEMENTS_PAGE_PATH}?template=${templateId}`);
    setOpenSearch(false);
  };

  const handleActionSelect = (value: string) => {
    if (value === "contact") {
      navigate(helpEntryPath);
      setOpenSearch(false);
    }
    else if (value === "logout") {
      dispatch(logout({ isVoluntary: true }));
      setOpenSearch(false);
    }
  };

  const groupedPages = useMemo(() => {
    return searchablePages.reduce((acc, page) => {
      if (!acc[page.category]) {
        acc[page.category] = [];
      }
      acc[page.category].push(page);
      return acc;
    }, {} as Record<string, typeof searchablePages>);
  }, [searchablePages]);

  return (
    <CommandDialog
      open={openSearch}
      onOpenChange={setOpenSearch}
    >
      <CommandInput placeholder={placeholderInput} />
      <CommandList>
        <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>

        {entryTemplates.length > 0 && (
          <CommandGroup heading="Signets d'écriture">
            {entryTemplates.map((tpl) => (
              <CommandItem
                key={tpl.id}
                value={tpl.name}
                keywords={[tpl.account_name ?? "", "signet", "écriture", "rapide", "modèle", "comptable", "template", "raccourci"]}
                onSelect={() => handleTemplateSelect(tpl.id)}
              >
                <Bookmark className="h-4 w-4" />
                <span className="flex-1">{tpl.name}</span>
                {tpl.account_name && <span className="text-xs text-muted-foreground">{tpl.account_name}</span>}
                <button
                  type="button"
                  className="ml-1 rounded p-0.5 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                  onClick={(e) => handleDeleteTemplate(e, tpl.id, tpl.name)}
                  title="Supprimer ce signet"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {Object.entries(groupedPages).map(([category, pages]) => (
          <CommandGroup
            key={category}
            heading={category}
          >
            {pages.map((page) => (
              <CommandItem
                key={page.value}
                value={`${page.value} ${page.label} ${page.keywords.join(" ")}`}
                onSelect={() => handleSelect(page.value)}
              >
                {page.locked
                  ? (
                    <Lock className="text-muted-foreground" size={14} />
                  )
                  : (
                    <StickyNote />
                  )}
                <span className={page.locked ? "text-muted-foreground" : ""}>
                  {page.label}
                </span>
                {page.locked && (
                  <span className="ml-auto text-[10px] font-semibold text-primary/80">
                    PRO
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />

        <CommandGroup heading="Actions">
          {actionItems.map((action) => (
            <CommandItem
              key={action.value}
              value={`${action.value} ${action.label} ${action.keywords.join(" ")}`}
              onSelect={() => handleActionSelect(action.value)}
            >
              {action.value === "contact" ? <Headset /> : <LogOut />}
              {action.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default Search;
